import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ResourceDirectory from '../components/ResourceDirectory';
import {
  formatDisplayDate,
  isUpcomingEvent,
  sortEventsAscending,
} from '../utils/dateUtils';

const Home = () => {
  const [events, setEvents] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingNeeds, setLoadingNeeds] = useState(true);
  const [eventsError, setEventsError] = useState(null);
  const [needsError, setNeedsError] = useState(null);
  const [availability, setAvailability] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubAvail = onSnapshot(
      doc(db, 'availability', 'settings'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          let showBanner = true;
          if (data.expirationDate) {
            const expireTime = data.expirationDate.toDate
              ? data.expirationDate.toDate()
              : new Date(data.expirationDate);
            if (new Date() > expireTime) {
              showBanner = false;
            }
          }
          setAvailability({
            ...data,
            showBanner: showBanner && data.status !== 'normal',
          });
        } else {
          setAvailability(null);
        }
      },
      (error) => {
        console.error('Error loading availability:', error);
        setAvailability(null);
      }
    );

    const eventsQuery = query(
      collection(db, 'events'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeEvents = onSnapshot(
      eventsQuery,
      (snapshot) => {
        const eventsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const upcoming = sortEventsAscending(
          eventsData.filter(isUpcomingEvent)
        );
        setEvents(upcoming);
        setEventsError(null);
        setLoadingEvents(false);
      },
      (error) => {
        console.error('Error fetching events:', error);
        setEventsError('Unable to load events right now. Please try again later.');
        setLoadingEvents(false);
      }
    );

    const needsQuery = query(
      collection(db, 'needs'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeNeeds = onSnapshot(
      needsQuery,
      (snapshot) => {
        const needsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNeeds(needsData);
        setNeedsError(null);
        setLoadingNeeds(false);
      },
      (error) => {
        console.error('Error fetching needs:', error);
        setNeedsError('Unable to load current needs right now. Please try again later.');
        setLoadingNeeds(false);
      }
    );

    return () => {
      unsubAvail();
      unsubscribeEvents();
      unsubscribeNeeds();
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-charcoal-dark text-slate-200">
      <Header />

      <main id="main-content" className="flex-grow max-w-6xl mx-auto w-full px-4 md:px-8 py-12">
        {availability && availability.showBanner && (
          <div className={`mb-8 p-4 rounded-lg border text-sm ${
            availability.status === 'unavailable' 
              ? 'bg-red-500/10 border-red-500/30 text-red-200' 
              : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200'
          }`}>
            <span className="font-bold uppercase tracking-wider block mb-1">
              ⚠️ Outreach Notice: {availability.status === 'unavailable' ? 'Service Temporarily Offline' : 'Response Times May Be Delayed'}
            </span>
            <p className="leading-relaxed">{availability.message}</p>
            {availability.expectedReviewDate && (
              <p className="text-xs text-slate-400 mt-2 font-bold">
                Expected review time: {new Date(availability.expectedReviewDate.toDate ? availability.expectedReviewDate.toDate() : availability.expectedReviewDate).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Hero Section */}
        <section className="flex flex-col items-center text-center mb-20 py-8 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-accent-purple/10 rounded-full blur-[80px] pointer-events-none z-0" />

          <div className="relative z-10 mb-8 transition-transform duration-500 hover:scale-[1.02]">
            <div className="absolute inset-0 bg-banana-yellow/10 rounded-full blur-xl animate-pulse" />
            <img
              src="/logo.png"
              alt="Tent Finder Compass Logo"
              className="w-48 sm:w-64 h-auto mx-auto drop-shadow-[0_0_35px_rgba(245,224,29,0.3)] relative z-10"
            />
          </div>

          <h2 className="text-4xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-4 uppercase font-display relative z-10 leading-none">
            Tent <span className="text-banana-yellow glow-yellow">Finder</span>
          </h2>

          <p className="text-base sm:text-lg md:text-xl text-slate-400 mb-8 max-w-2xl mx-auto tracking-wide uppercase font-medium relative z-10">
            Locate the Displaced. Deploy the Comfort. No Accidents.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full max-w-md mx-auto relative z-10">
            <button
              type="button"
              onClick={() => navigate('/help')}
              disabled={availability != null && availability.acceptVolunteerOffers !== true}
              title={
                availability != null && availability.acceptVolunteerOffers !== true
                  ? 'Volunteer intake is temporarily closed'
                  : undefined
              }
              className="cyber-btn-yellow w-full disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              I Want to Help
            </button>
            <button
              type="button"
              onClick={() => navigate('/donate')}
              className="cyber-btn-purple w-full"
            >
              Donate Now
            </button>
          </div>

          <div className="flex gap-4 justify-center mt-6 w-full max-w-md mx-auto relative z-10">
            <button
              type="button"
              onClick={() => navigate('/get-help')}
              disabled={availability != null && availability.acceptHelpRequests !== true}
              title={
                availability != null && availability.acceptHelpRequests !== true
                  ? 'Help request intake is temporarily closed'
                  : undefined
              }
              className="cyber-btn-orange w-full py-2.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Get Help!
            </button>
            <button
              type="button"
              onClick={() => navigate('/report-camp')}
              disabled={availability != null && availability.acceptCampReports !== true}
              title={
                availability != null && availability.acceptCampReports !== true
                  ? 'Camp reporting is temporarily closed'
                  : undefined
              }
              className="cyber-btn-blue w-full py-2.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Report Camp
            </button>
          </div>
          {availability != null &&
            (availability.acceptHelpRequests !== true ||
              availability.acceptCampReports !== true ||
              availability.acceptVolunteerOffers !== true) && (
            <p className="mt-4 text-xs text-slate-500 max-w-md mx-auto relative z-10" role="status">
              Some online intake channels are closed. Emergency: call{' '}
              <a href="tel:911" className="text-urgent-orange font-semibold hover:underline">911</a>
              {' '}or{' '}
              <a href="tel:211" className="text-banana-yellow font-semibold hover:underline">211</a>
              {' '}for community resources.
            </p>
          )}
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 relative z-10">
          {/* Current Needs */}
          <section className="order-1 md:order-2" aria-busy={loadingNeeds}>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2.5 h-6 bg-banana-yellow block rounded-sm shadow-[0_0_10px_#F5E01D]"></span>
              <h2 className="text-2xl font-bold uppercase tracking-tight">
                Current Needs
              </h2>
            </div>
            {loadingNeeds ? (
              <p className="text-slate-400 bg-charcoal-card border border-white/5 p-6 rounded-lg shadow-xl">
                Loading needs...
              </p>
            ) : needsError ? (
              <p className="text-urgent-orange bg-charcoal-card border border-urgent-orange/20 p-6 rounded-lg shadow-xl" role="alert">
                {needsError}
              </p>
            ) : needs.length === 0 ? (
              <p className="text-slate-400 bg-charcoal-card border border-white/5 p-6 rounded-lg shadow-xl">
                No current needs listed. Check back soon!
              </p>
            ) : (
              <div className="chrome-panel border border-white/5 rounded-lg p-6 space-y-4">
                <ul className="space-y-4">
                  {needs.map((need) => (
                    <li
                      key={need.id}
                      className="flex items-start border-b border-white/5 pb-4 last:border-b-0 last:pb-0"
                    >
                      <span className="text-banana-yellow mr-3 font-bold text-lg" aria-hidden="true">
                        •
                      </span>
                      <div>
                        <strong className="text-white text-base font-bold uppercase block mb-1">
                          {need.title}
                        </strong>
                        <span className="text-slate-400 text-sm whitespace-pre-line">
                          {need.description}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Upcoming Events */}
          <section className="order-2 md:order-1" aria-busy={loadingEvents}>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2.5 h-6 bg-accent-cyan block rounded-sm shadow-[0_0_10px_#00F0FF]"></span>
              <h2 className="text-2xl font-bold uppercase tracking-tight">
                Upcoming Events
              </h2>
            </div>
            {loadingEvents ? (
              <p className="text-slate-400 bg-charcoal-card border border-white/5 p-6 rounded-lg shadow-xl">
                Loading events...
              </p>
            ) : eventsError ? (
              <p className="text-urgent-orange bg-charcoal-card border border-urgent-orange/20 p-6 rounded-lg shadow-xl" role="alert">
                {eventsError}
              </p>
            ) : events.length === 0 ? (
              <p className="text-slate-400 bg-charcoal-card border border-white/5 p-6 rounded-lg shadow-xl">
                No upcoming events at this time. Check back soon!
              </p>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="chrome-panel border border-white/5 rounded-lg p-6 hover:border-accent-cyan/20 transition-all duration-300 hover:scale-[1.01]"
                  >
                    <h3 className="text-lg font-bold text-accent-cyan mb-2 uppercase">
                      {event.title}
                    </h3>
                    <p className="text-slate-300 text-sm mb-3 whitespace-pre-line leading-relaxed">
                      {event.description}
                    </p>
                    {event.date && (
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">
                        📅 {formatDisplayDate(event.date)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <ResourceDirectory />
      </main>

      <Footer />
    </div>
  );
};

export default Home;
